/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import styled from "styled-components";
import { Text, theme } from "@nordcloud/gnui";
import {
  CloudWasteSettingStatus,
  CloudWasteSettingKey,
  CloudWasteSettingCategorised,
  CloudWasteSetting,
} from "~/generated/graphql";
import { isNotNil } from "~/tools";
import { useFormState, useTurbonomicPolicySettings } from "./hooks";
import { BoxSwitchInput, CustomNumberInput } from "./Inputs";
import {
  SettingParameter,
  SettingParameterInt,
  UpdateSetting,
  UpdateSettingParameter,
} from "./types";
import { CategoryLabels, isIntParameter } from "./utils";

type SettingContainerProps = {
  status: CloudWasteSettingStatus;
};

const SettingContainer = styled.div<SettingContainerProps>`
  margin-bottom: ${theme.spacing.spacing02};
  opacity: ${({ status }) =>
    status === CloudWasteSettingStatus.Enabled ? 1 : 0.6};

  &:last-of-type {
    margin-bottom: 0;
  }
`;

type CloudWasteSettingsFormProps = {
  onSubmit: (data: UpdateSetting[]) => void;
  initialData: CloudWasteSettingCategorised[];
  disabled?: boolean;
  // prop to sygnalize parent that some changes have been made
  onChange: () => void;
};

export function CloudWasteSettingsForm({
  onSubmit,
  initialData,
  onChange,
  disabled = false,
}: CloudWasteSettingsFormProps) {
  const { data, error, handlers } = useFormState(initialData);
  const handleSubmit = (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    handlers.handleSubmit(onSubmit);
  };

  return (
    <form id="cloud-waste-settings" onSubmit={handleSubmit}>
      {data.map(({ category, settings }) => (
        <div key={category} css={{ marginBottom: theme.spacing.spacing04 }}>
          <Text mb={theme.spacing.spacing02} color={theme.color.text.text02}>
            {CategoryLabels[category]}
          </Text>
          {settings.map((setting) => (
            <PolicySettings
              key={setting.id}
              item={setting}
              error={error}
              handlers={handlers}
              disabled={disabled}
              onChange={onChange}
            />
          ))}
        </div>
      ))}
    </form>
  );
}

type PolicySettingsProps = {
  item: Omit<CloudWasteSetting, "parameters"> & {
    parameters: (Omit<SettingParameter, "value"> & UpdateSettingParameter)[];
  };
  error: ReturnType<typeof useFormState>["error"];
  handlers: ReturnType<typeof useFormState>["handlers"];
  disabled?: boolean;
  onChange: () => void;
};

function PolicySettings({
  item,
  error,
  handlers,
  onChange,
  disabled,
}: PolicySettingsProps) {
  const {
    disabledDescription,
    disableTurbonomicSettings,
    loading,
    turbonomicError,
  } = useTurbonomicPolicySettings(item);

  const description = disableTurbonomicSettings
    ? disabledDescription
    : item.description;

  const combinedError =
    turbonomicError !== undefined ? { ...error, ...turbonomicError } : error;

  return (
    <SettingContainer status={item.status}>
      <BoxSwitchInput
        checked={item.status === CloudWasteSettingStatus.Enabled}
        disabled={disabled || disableTurbonomicSettings}
        label={item.name}
        description={description}
        data-testid={`switch-input-${item.status}`}
        loading={loading}
        onChange={() => {
          handlers.handleStatusChange(item.id);
          onChange();
        }}
      >
        <When
          condition={
            isNotNil(item.parameters) &&
            item.status === CloudWasteSettingStatus.Enabled
          }
        >
          <PolicyParameters
            parameters={item.parameters}
            settingId={item.id}
            handlers={handlers}
            disabled={disabled}
            onChange={onChange}
          />
        </When>
        {combinedError?.[item.id] && (
          <Text mb="0" mt={theme.spacing.spacing01} color="danger">
            {combinedError[item.id]}
          </Text>
        )}
      </BoxSwitchInput>
    </SettingContainer>
  );
}

type PolicyParametersProps = {
  parameters: (Omit<SettingParameterInt, "value"> & UpdateSettingParameter)[];
  settingId: CloudWasteSettingKey;
  handlers: ReturnType<typeof useFormState>["handlers"];
  disabled?: boolean;
  onChange: () => void;
};

function PolicyParameters({
  parameters,
  settingId,
  handlers,
  onChange,
  disabled,
}: PolicyParametersProps) {
  return (
    <>
      {parameters.map((parameter) => (
        <When key={parameter.id} condition={isIntParameter(parameter)}>
          <CustomNumberInput
            id={settingId + parameter.id}
            label={parameter.description}
            value={parameter.value}
            disabled={disabled}
            unit={parameter.id.toLowerCase()}
            tooltipCaption={`Choose value between ${parameter.min} and ${parameter.max}`}
            onChange={(e) => {
              handlers.handleInputChange(
                settingId,
                parameter.id,
                e.target.value ?? parameter.value
              );
              onChange();
            }}
          />
        </When>
      ))}
    </>
  );
}
